import React, { useState } from "react";
import Card from "components/card";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import { createEvent } from "services/events";
import { useToast } from "components/Toast";

const CreateEvent = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState<number | "">("");
  const [enableRsvp, setEnableRsvp] = useState(true);
  const [enableWaitlist, setEnableWaitlist] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createEvent({
        name,
        description,
        event_date: eventDate,
        event_time: eventTime,
        location,
        capacity: capacity ? parseInt(String(capacity)) : undefined,
        enable_rsvp: enableRsvp,
        enable_waitlist: enableWaitlist,
      });
      navigate("/admin/events");
      toast.showSuccess("Event created");
    } catch (err) {
      console.error(err);
      const anyE = err as any;
      let message =
        anyE?.response?.data?.message || anyE?.message || String(err);
      if (anyE?.response?.data?.errors) {
        message = Object.values(anyE.response.data.errors).flat()[0] || message;
      }
      toast.showError(message || "Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card extra="w-full p-4">
      <h2 className="mb-4 text-2xl font-bold">Create Event</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 placeholder-gray-400 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-white/40"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={4}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 placeholder-gray-400 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-white/40"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 placeholder-gray-400 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-white/40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Time
            </label>
            <input
              type="time"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 placeholder-gray-400 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-white/40"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Location
          </label>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 placeholder-gray-400 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-white/40"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Capacity (Optional)
          </label>
          <input
            type="number"
            min="1"
            value={capacity}
            onChange={(e) =>
              setCapacity(e.target.value ? parseInt(e.target.value) : "")
            }
            placeholder="Leave empty for unlimited"
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 placeholder-gray-400 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-white/40"
          />
        </div>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableRsvp"
              checked={enableRsvp}
              onChange={(e) => setEnableRsvp(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <label
              htmlFor="enableRsvp"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Enable RSVP
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableWaitlist"
              checked={enableWaitlist}
              onChange={(e) => setEnableWaitlist(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <label
              htmlFor="enableWaitlist"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Enable Waitlist
            </label>
          </div>
        </div>
        <div>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating..." : "Create"}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default CreateEvent;
